import { motion } from "framer-motion";
import { Award, Users, Clock, CheckCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export const About = () => {
  const { language, isRTL } = useLanguage();

  const content = {
    ar: {
      badge: "من نحن",
      title: "مجموعة الفنون للصناعات الحديثة",
      paragraphs: [
        "مجموعة الفنون للصناعة هي واحدة من الشركات الرائدة في المملكة العربية السعودية في مجال التصنيع والتصميم. نحن نجمع بين الخبرة العريقة والتقنيات الحديثة لتقديم منتجات عالية الجودة تلبي تطلعات عملائنا.",
        "نتخصص في ستة مجالات رئيسية: صناعة الأخشاب، وتصنيع الألمنيوم، وأعمال الحديد، والزجاج، والرخام، والاستانلس ستيل. نفخر بتقديم حلول متكاملة تجمع بين الجمال والمتانة والوظيفة العملية.",
        "نعمل بشغف وإبداع لتحويل رؤية عملائنا إلى واقع ملموس، مع الالتزام بأعلى معايير الجودة والدقة في التنفيذ."
      ],
      features: ["جودة عالية", "أسعار منافسة", "ضمان شامل", "دعم متواصل"],
      stats: [
        { value: "+10", label: "سنوات خبرة" },
        { value: "+500", label: "عميل راضي" },
        { value: "+750", label: "مشروع منجز" },
        { value: "100%", label: "جودة مضمونة" }
      ]
    },
    en: {
      badge: "About Us",
      title: "ALFUNOON Modern Industries Group",
      paragraphs: [
        "ALFUNOON Industries Group is one of the leading companies in Saudi Arabia in manufacturing and design. We combine deep expertise with modern technologies to deliver high-quality products that meet our clients' aspirations.",
        "We specialize in six main areas: wood manufacturing, aluminum fabrication, iron works, glass, marble, and stainless steel. We take pride in providing integrated solutions that combine beauty, durability, and functionality.",
        "We work with passion and creativity to transform our clients' vision into reality, while maintaining the highest standards of quality and precision in execution."
      ],
      features: ["Premium Quality", "Competitive Prices", "Comprehensive Warranty", "Ongoing Support"],
      stats: [
        { value: "+10", label: "Years Experience" },
        { value: "+500", label: "Happy Clients" },
        { value: "+750", label: "Completed Projects" },
        { value: "100%", label: "Guaranteed Quality" }
      ]
    }
  };

  const t = content[language];
  const icons = [Clock, Users, Award, CheckCircle];

  return (
    <section className="py-20 bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t.badge}
            </span>
            <h2 className="text-3xl md:text-4xl font-kufi font-bold mb-6 text-foreground">
              {t.title}
            </h2>
            <div className="space-y-4 text-muted-foreground font-kufi leading-relaxed">
              {t.paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            {/* Features List */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              {t.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span className="font-kufi text-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Stats Side */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-6"
          >
            {t.stats.map((stat, index) => {
              const Icon = icons[index];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-card rounded-2xl p-6 shadow-lg border border-border hover:shadow-xl transition-shadow duration-300 text-center"
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-3xl font-bold text-primary mb-2">{stat.value}</h3>
                  <p className="text-muted-foreground font-kufi">{stat.label}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
